/**
 * Socket-based assembly.
 * Takes a layout definition and a module library, instantiates each module,
 * and positions them by linking sockets head-to-tail.
 *
 * Convention:
 *  - Each module declares sockets in local coordinates (relative to its origin).
 *  - When attaching module B to socket S of module A, we position B so that
 *    B's matching socket (named "left" if A's socket is "right" etc.) is at
 *    the world position of A's socket.
 *  - Rotation: if A's socket has a rotation hint, B is rotated accordingly.
 */

import * as THREE from "three";
import type { ModuleDef, ProductConfig } from "@/types";
import { loadModule, type LoadedModule } from "./ModuleLoader";

const OPPOSITE_SOCKETS: Record<string, string> = {
  right: "left",
  left: "right",
  front: "back",
  back: "front",
  top: "bottom",
  bottom: "top",
};

export interface AssemblyResult {
  group: THREE.Group;
  modules: Map<string, LoadedModule>;
  /** Bounding box for camera framing. */
  bbox: THREE.Box3;
  /** Module ids that declared a GLB but failed to load (procedural placeholder in
   *  their place). Non-empty = degraded build the engine must not display. */
  failedModules: string[];
}

/**
 * Build an assembly from the product config + current layout + size.
 */
export async function buildAssembly(
  product: ProductConfig,
  formeId: string,
  /** Optional GLB url that replaces every module's glb for this build — drives the
   *  studio-lighting per-colorway GLB swap (each fabric ships its own baked GLB). */
  glbOverride?: string,
): Promise<AssemblyResult> {
  const forme = product.formes.find((f) => f.id === formeId);
  if (!forme) throw new Error(`Forme not found: ${formeId}`);

  const moduleDefs = new Map<string, ModuleDef>(
    product.modules.map((m) => [m.id, glbOverride ? { ...m, glb: glbOverride } : m]),
  );

  const group = new THREE.Group();
  group.name = `assembly:${formeId}`;

  const instances = new Map<string, LoadedModule>();
  const failedModules: string[] = [];

  for (const instance of forme.modules) {
    const def = moduleDefs.get(instance.moduleId);
    if (!def) {
      console.warn(`Module def missing for ${instance.moduleId}, skipping`);
      continue;
    }
    const loaded = await loadModule(def);
    if (loaded.failed) failedModules.push(instance.moduleId);

    if (instance.position) {
      // Absolute placement (non-socketed kits like Quaternius). Bypasses socket logic.
      loaded.object.position.set(
        instance.position.x,
        instance.position.y,
        instance.position.z,
      );
      if (typeof instance.rotationY === "number") {
        loaded.object.rotation.y = (instance.rotationY * Math.PI) / 180;
      }
      group.add(loaded.object);
    } else if (instance.attachTo) {
      const parent = instances.get(instance.attachTo.moduleId);
      if (!parent) {
        console.warn(
          `Attach target module ${instance.attachTo.moduleId} not yet instantiated, placing at origin`,
        );
        group.add(loaded.object);
      } else {
        attachToSocket(parent, loaded, instance.attachTo.socketName);
        group.add(loaded.object);
      }
    } else {
      group.add(loaded.object);
    }
    instances.set(instance.moduleId, loaded);
  }

  // Center on origin for nice framing
  const bbox = new THREE.Box3().setFromObject(group);
  const center = bbox.getCenter(new THREE.Vector3());
  group.position.x -= center.x;
  group.position.z -= center.z;
  // Recompute after re-center
  const finalBox = new THREE.Box3().setFromObject(group);

  return { group, modules: instances, bbox: finalBox, failedModules };
}

function attachToSocket(
  parent: LoadedModule,
  child: LoadedModule,
  parentSocketName: string,
): void {
  const parentSocket = findSocket(parent.object, parentSocketName);
  if (!parentSocket) {
    console.warn(`Socket '${parentSocketName}' not found on ${parent.def.id}`);
    return;
  }
  const oppositeName = OPPOSITE_SOCKETS[parentSocketName] ?? parentSocketName;
  const childSocket = findSocket(child.object, oppositeName);

  // World position of parent socket
  parent.object.updateMatrixWorld();
  const targetWorldPos = new THREE.Vector3();
  parentSocket.getWorldPosition(targetWorldPos);

  // Apply parent socket rotation hint (if any) to child
  const parentDef = parent.def;
  const socketDef = parentDef.sockets.find((s) => s.name === parentSocketName);
  if (socketDef?.rotation) {
    child.object.rotation.set(
      (socketDef.rotation.x * Math.PI) / 180,
      (socketDef.rotation.y * Math.PI) / 180,
      (socketDef.rotation.z * Math.PI) / 180,
    );
  }

  // Compute child offset so that its opposing socket lines up with target
  if (childSocket) {
    child.object.updateMatrixWorld();
    const childSocketLocal = new THREE.Vector3();
    childSocket.getWorldPosition(childSocketLocal);
    // childSocketLocal is in world space, but since child has not been added yet
    // its world matrix equals its local matrix. We position the child such that
    // (child.position + childSocketLocalOffset_rotated) equals parentSocketWorldPos.
    const localOffset = childSocket.position.clone();
    // Rotate localOffset by child's current rotation
    const rotated = localOffset.clone().applyEuler(child.object.rotation);
    child.object.position.copy(targetWorldPos).sub(rotated);
  } else {
    // No matching socket on child: place its origin at the parent socket position
    child.object.position.copy(targetWorldPos);
  }
}

function findSocket(obj: THREE.Object3D, name: string): THREE.Object3D | null {
  let found: THREE.Object3D | null = null;
  obj.traverse((o) => {
    if (!found && o.name === `socket:${name}`) {
      found = o;
    }
  });
  return found;
}

/**
 * Detach an assembly from the scene and free the resources it OWNS.
 *
 * GLB-sourced geometry + materials are NOT ours: `clone(true)` shares them with
 * the ModuleLoader cache, across every clone and every rebuild. Disposing them
 * here frees GPU buffers the cache still points at, forcing a redundant re-upload
 * on the next switch (finding #2). They are released once, at engine teardown,
 * by clearModuleCache(). So here we only dispose procedural, per-instance
 * resources (the fallback geometry), and always detach the group.
 */
export function disposeAssembly(assembly: AssemblyResult): void {
  assembly.group.traverse((o) => {
    if (o.userData?.fromGLB) return; // cache-owned, shared — leave to clearModuleCache()
    const mesh = o as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[];
    if (Array.isArray(mat)) {
      for (const m of mat) m.dispose();
    } else if (mat) {
      mat.dispose();
    }
  });
  assembly.group.removeFromParent();
}
